import { supabase } from '../lib/supabase';

// ─── Center Memberships ───────────────────────────────────────────────────────

export async function getCenterMemberships(centerId) {
  try {
    let query = supabase?.from('center_memberships')?.select('*, user_profiles(id, full_name, email, phone, role, global_role)');

    if (centerId) {
      query = query?.eq('center_id', centerId);
    }

    const { data, error } = await query?.order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  } catch (err) {
    console.error('getCenterMemberships error:', err);
    return [];
  }
}

export async function getUserMemberships(userId) {
  try {
    const { data, error } = await supabase?.from('center_memberships')?.select('*, centers(id, name, city, status)')?.eq('user_id', userId);
    if (error) throw error;
    return data || [];
  } catch (err) {
    console.error('getUserMemberships error:', err);
    return [];
  }
}

export async function addCenterMembership({ userId, centerId, role }) {
  try {
    const { data, error } = await supabase?.from('center_memberships')?.insert({ user_id: userId, center_id: centerId, role })?.select()?.single();
    if (error) throw error;
    return { data, error: null };
  } catch (err) {
    return { data: null, error: err };
  }
}

export async function updateCenterMembership(id, updates) {
  try {
    const { data, error } = await supabase?.from('center_memberships')?.update(updates)?.eq('id', id)?.select()?.single();
    if (error) throw error;
    return { data, error: null };
  } catch (err) {
    return { data: null, error: err };
  }
}

export async function removeCenterMembership(id) {
  try {
    const { error } = await supabase?.from('center_memberships')?.delete()?.eq('id', id);
    if (error) throw error;
    return { error: null };
  } catch (err) {
    return { error: err };
  }
}

export async function getCurrentUserMemberships() {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) return [];
    return getUserMemberships(user?.id);
  } catch (err) {
    console.error('getCurrentUserMemberships error:', err);
    return [];
  }
}

// ─── Agreements ───────────────────────────────────────────────────────────────

export async function getAgreements(centerId) {
  try {
    let query = supabase?.from('agreements')?.select('*, centers(name), inventory(unit_number, unit_name, floor)');

    if (centerId) {
      query = query?.eq('center_id', centerId);
    }

    const { data, error } = await query?.order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  } catch (err) {
    console.error('getAgreements error:', err);
    return [];
  }
}

export async function createAgreement(agreementData) {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    const { data, error } = await supabase?.from('agreements')?.insert({ ...agreementData, created_by: user?.id })?.select()?.single();
    if (error) throw error;
    return { data, error: null };
  } catch (err) {
    return { data: null, error: err };
  }
}

export async function updateAgreement(id, updates) {
  try {
    const { data, error } = await supabase?.from('agreements')?.update(updates)?.eq('id', id)?.select()?.single();
    if (error) throw error;
    return { data, error: null };
  } catch (err) {
    return { data: null, error: err };
  }
}

export async function uploadAgreementFile(file, agreementId) {
  try {
    const fileExt = file?.name?.split('.')?.pop();
    const filePath = `${agreementId}/${Date.now()}-${file?.name}`;

    const { error: uploadError } = await supabase?.storage?.from('agreements')?.upload(filePath, file, { cacheControl: '3600', upsert: false });

    if (uploadError) throw uploadError;
    return { filePath, error: null };
  } catch (err) {
    return { filePath: null, error: err };
  }
}

export async function getAgreementFileUrl(filePath) {
  try {
    const { data, error } = await supabase?.storage?.from('agreements')?.createSignedUrl(filePath, 3600);
    if (error) throw error;
    return { url: data?.signedUrl, error: null };
  } catch (err) {
    return { url: null, error: err };
  }
}

// ─── Prediction V1 ───────────────────────────────────────────────────────────

export async function getCenterForecast(centerId) {
  try {
    const { data, error } = await supabase?.rpc('get_center_forecast', {
      p_center_id: centerId
    });
    if (error) throw error;
    return data || {};
  } catch (err) {
    console.error('getCenterForecast error:', err);
    return {};
  }
}

export async function getCenterHealthScore(centerId) {
  try {
    const { data, error } = await supabase?.rpc('get_center_health_score', {
      p_center_id: centerId
    });
    if (error) throw error;
    return data || {};
  } catch (err) {
    console.error('getCenterHealthScore error:', err);
    return {};
  }
}

export async function getAllCentersHealth() {
  try {
    const { data: centers, error } = await supabase?.from('centers')?.select('id, name, city')?.eq('is_active', true);
    if (error) throw error;

    const healthData = await Promise.all(
      (centers || [])?.map(async (center) => {
        const health = await getCenterHealthScore(center?.id);
        const forecast = await getCenterForecast(center?.id);
        return { ...center, health, forecast };
      })
    );
    return healthData;
  } catch (err) {
    console.error('getAllCentersHealth error:', err);
    return [];
  }
}
